import { Badge } from "@/components/ui/badge";
import {
  ShieldAlert, Clock, Flame, Snowflake, Eye, Zap, Heart, Ear,
  Ghost, Hand, Ban, EyeOff, Lock, Mountain, Skull, ArrowDown,
  Link, Brain, Sparkles, AlertTriangle,
} from "lucide-react";

interface Condition {
  name: string;
  duration_turns?: number;
  source?: string;
  exhaustion_level?: number;
}

interface ConditionsWidgetProps {
  conditions: Condition[] | null;
}

const CONDITION_ICONS: Record<string, typeof ShieldAlert> = {
  blinded: EyeOff,
  charmed: Heart,
  deafened: Ear,
  frightened: Ghost,
  grappled: Hand,
  incapacitated: Ban,
  invisible: Eye,
  paralyzed: Lock,
  petrified: Mountain,
  poisoned: Flame,
  prone: ArrowDown,
  restrained: Link,
  stunned: Zap,
  unconscious: Skull,
  frozen: Snowflake,
  concentration: Brain,
  exhaustion: AlertTriangle,
  exhaustion_1: AlertTriangle,
  exhaustion_2: AlertTriangle,
  exhaustion_3: AlertTriangle,
  exhaustion_4: AlertTriangle,
  exhaustion_5: AlertTriangle,
  exhaustion_6: Skull,
};

function getExhaustionLevel(c: Condition): number | null {
  if (c.exhaustion_level) return c.exhaustion_level;
  const match = c.name.match(/exhaustion[_\s]*(\d)/i);
  return match ? parseInt(match[1]) : c.name.toLowerCase() === "exhaustion" ? 1 : null;
}

export function ConditionsWidget({ conditions }: ConditionsWidgetProps) {
  if (!conditions || conditions.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {conditions.map((c, i) => {
        const key = c.name.toLowerCase().replace(/\s+/g, "_");
        const exhaustionLvl = getExhaustionLevel(c);
        const isExhaustion = exhaustionLvl !== null;
        const isConcentration = key === "concentration";
        const Icon = CONDITION_ICONS[key] || ShieldAlert;

        const variant = isExhaustion && exhaustionLvl >= 4 ? "destructive" as const
          : isConcentration ? "default" as const
          : "destructive" as const;

        return (
          <Badge
            key={`${c.name}-${i}`}
            variant={variant}
            className="text-[10px] gap-1 px-1.5 py-0.5"
          >
            <Icon className="w-3 h-3" />
            {isExhaustion ? `Exhaust L${exhaustionLvl}` : c.name}
            {isConcentration && c.source && (
              <span className="opacity-75 ml-0.5">({c.source})</span>
            )}
            {c.duration_turns != null && c.duration_turns > 0 && (
              <span className="flex items-center gap-0.5 opacity-75">
                <Clock className="w-2.5 h-2.5" />
                {c.duration_turns}
              </span>
            )}
          </Badge>
        );
      })}
    </div>
  );
}
